import { defineComponent as _defineComponent } from 'vue'
import { Mode } from '@/models/enums/Mode';
import { ShiftModel } from '@/models/shiftModel';
import { shiftsService } from '@/services/shifts.service';
import { Shift } from '@/models/shift';
import { ShiftOverflowEnum } from '@/models/enums/ShiftOverflowEnum';
import { Customer } from '@/models/tenant';
import { customersService } from '@/services/tenants.service';
import i18n from '@/i18n';
import { computed, onMounted, reactive, ref } from 'vue';
import { values } from '@/utils/enum';
import useVuelidate from '@vuelidate/core';
import {
  maxLength,
  required,
  withLocalizedValidators,
} from '@/utils/validation';
import { useI18n } from '@/composables/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftFormModal',
  props: {
    customer: null,
    shiftModel: null,
    mode: null,
    shift: null
  },
  emits: ['save', 'close'],
  setup(__props: any, { emit }) {

const props = __props as {
  customer: Customer;
  shiftModel: ShiftModel;
  mode: Mode;
  shift: Shift;
};





const isSubmitting = ref(false);
const editableShift = reactive(Shift.GetEmpty());
const overflowTypes = values(ShiftOverflowEnum).filter(
  (value) => value !== ShiftOverflowEnum.Unspecified,
);

const validations = {
  name: withLocalizedValidators('shift_model.shift.name_field', {
    required,
    maxLength: maxLength(256),
  }),
  overflow: withLocalizedValidators('shift_model.shift.overflow.label', {
    required: (value: ShiftOverflowEnum) =>
      !requiresOverflowConfiguration.value ||
      [ShiftOverflowEnum.NextDay, ShiftOverflowEnum.PreviousDay].includes(
        value,
      ),
  }),
};
const v$ = useVuelidate(validations, editableShift);
const { t } = useI18n();

onMounted(() => {
  if (isCreateMode.value) {
    editableShift.shiftModelId = props.shiftModel.id;
  } else {
    Object.assign(editableShift, props.shift);
  }
});

function handleTimeFromChange(value: Date): void {
  editableShift.timeFromHh = value.getHours();
  editableShift.timeFromMm = value.getMinutes();
  updateOverflow();
}

function handleTimeToChange(value: Date): void {
  editableShift.timeToHh = value.getHours();
  editableShift.timeToMm = value.getMinutes();
  updateOverflow();
}

function updateOverflow() {
  if (!requiresOverflowConfiguration.value) {
    editableShift.overflow = ShiftOverflowEnum.Unspecified;
  }
}

async function onSubmit() {
  if (editableShift.overflow === null) {
    editableShift.overflow = ShiftOverflowEnum.Unspecified;
  }
  isSubmitting.value = true;
  try {
    if (isCreateMode.value) {
      await shiftsService.create(editableShift).then(async () => {
        if (props.customer.id === customersService.store.current()?.id) {
          // When will this be false?
          await shiftsService.getByTenantId(
            customersService.store.current()!.id,
          );
        }
      });
    } else if (isEditMode.value) {
      await shiftsService.update(editableShift).then(async () => {
        if (props.customer.id === customersService.store.current()?.id) {
          await shiftsService.store.update(editableShift);
        }
      });
    }
    emit('save');
    emit('close');
  } finally {
    isSubmitting.value = false;
  }
}

function getErrorMessage(key: keyof typeof editableShift) {
  return v$.value[key].$errors?.[0]?.$message;
}

const requiresOverflowConfiguration = computed((): boolean => {
  if (editableShift.timeToHh === 0 && editableShift.timeToMm === 0) {
    return false;
  }
  return (
    editableShift.timeFromHh > editableShift.timeToHh ||
    (editableShift.timeFromHh === editableShift.timeToHh &&
      editableShift.timeFromMm > editableShift.timeToMm)
  );
});

const isEditMode = computed((): boolean => props.mode === Mode.Edit);
const isCreateMode = computed((): boolean => props.mode === Mode.Create);

const timeFromDate = computed((): Date => {
  const date = new Date();
  date.setHours(editableShift.timeFromHh);
  date.setMinutes(editableShift.timeFromMm);
  return date;
});

const timeToDate = computed((): Date => {
  const date = new Date();
  date.setHours(editableShift.timeToHh);
  date.setMinutes(editableShift.timeToMm);
  return date;
});

const actionTitle = computed(() => {
  if (isCreateMode.value) {
    return i18n.t('action.create').toString();
  } else if (isEditMode.value) {
    return i18n.t('action.edit').toString();
  } else {
    return i18n.t('action.close').toString();
  }
});

return { __sfc: true,props, emit, isSubmitting, editableShift, overflowTypes, validations, v$, t, handleTimeFromChange, handleTimeToChange, updateOverflow, onSubmit, getErrorMessage, requiresOverflowConfiguration, isEditMode, isCreateMode, timeFromDate, timeToDate, actionTitle }
}

})